import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import { getService, recordVisit, planKiosk, unplanKiosk } from "../../actions/service";
import { canAccess } from "../../utils/access";
import { money } from "../tenant/tenantLabels";
import { primaryButton, smallButton } from "../template/templateLabels";
import VisitForm from "./VisitForm";

// The service plan (the backend's routes/api/service.js and utils/service.js): on
// which day to go to which kiosk for its A4 paper and ink. Stops that bring more money
// come first. A worker records what they did at a kiosk at any time and in any order,
// may do a kiosk sooner or move it to another day, and may plan any kiosk. A role
// with service.assign may also order a day and what to do there, which the others
// cannot move; it stays until visits have done all of it.

const REFRESH_MS = 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ["Ням", "Даваа", "Мягмар", "Лхагва", "Пүрэв", "Баасан", "Бямба"];

const offsetOf = (day, today) => Math.round((Date.parse(day) - Date.parse(today)) / DAY_MS);
const weekday = (day) => WEEKDAYS[new Date(day + "T00:00:00Z").getUTCDay()];
const dayName = (day, today) => {
	const offset = offsetOf(day, today);
	if (offset === 0) return "Өнөөдөр";
	if (offset === 1) return "Маргааш";
	if (offset === 2) return "Нөгөөдөр";
	return weekday(day);
};
const dayLabel = (day, today) => dayName(day, today) + " " + day.slice(5);
// A visit's time is real UTC; it shows as its day in Ulaanbaatar
const ubDayOf = (date) => new Date(new Date(date).getTime() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);

const NEEDS = { paper: "A4 цаас", black: "Хар бэх", color: "Өнгөт бэх" };
const KINDS = Object.keys(NEEDS);
const kindsText = (kinds) => kinds.map((kind) => NEEDS[kind]).join(", ");
// What an admin ordered done at the kiosk and visits have not done yet
const orderOf = (task) => (task && task.plan && task.plan.ordered ? task.plan.todo || [] : []);

const needText = (need) => {
	if (need.daysLeft <= 0) return NEEDS[need.kind] + (need.kind === "paper" ? " цөөрсөн" : " дууссан");
	const days = Math.floor(need.daysLeft);
	return NEEDS[need.kind] + (days < 1 ? " өнөөдөр дуусна" : ` ${days} хоног`);
};

const needClass = (need) => {
	if (need.daysLeft <= 1) return "text-red-700 bg-red-100 dark:bg-red-700 dark:text-red-100";
	if (need.daysLeft <= 3) return "text-yellow-700 bg-yellow-100 dark:bg-yellow-700 dark:text-yellow-100";
	return "text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-200";
};

const Chip = ({ className, children }) => (
	<span className={"inline-block px-2 py-1 mr-2 mt-2 text-xs font-semibold leading-tight rounded-full " + className}>
		{children}
	</span>
);

// What to take along for the day's kiosks: what they need, and what was ordered
const packingOf = (stops) => {
	const counts = { paper: 0, black: 0, color: 0 };
	stops.forEach((stop) =>
		stop.kiosks.forEach((task) =>
			new Set([...task.needs.map((need) => need.kind), ...orderOf(task)]).forEach((kind) => (counts[kind] += 1))
		)
	);
	return Object.keys(counts)
		.filter((kind) => counts[kind] > 0)
		.map((kind) => (kind === "paper" ? `A4 цаас ${counts[kind]} киоскт` : `${NEEDS[kind]} ${counts[kind]}`));
};

// The day picker of a kiosk: its day, the plan's other days, and back to the one
// worked out from its paper and ink
const pickerClass =
	"h-8 py-0 pl-2 pr-8 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 focus:border-purple-400 focus:outline-none";

const DayPicker = ({ value, days, today, disabled, onChange }) => (
	<select
		className={pickerClass}
		value={value || ""}
		disabled={disabled}
		onChange={(e) => onChange(e.target.value)}
	>
		{!value && <option value="">Өдөр сонгох</option>}
		{days.map((item) => (
			<option key={item.day} value={item.day}>
				{dayLabel(item.day, today)}
			</option>
		))}
	</select>
);

const kindClass = (on) =>
	"px-3 py-1 text-xs font-medium leading-5 border rounded-lg focus:outline-none " +
	(on
		? "text-white bg-purple-600 border-purple-600"
		: "text-gray-700 bg-white border-gray-300 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600");

// What an order is for, picked before it is made or changed
const OrderPicker = ({ initial, busy, onSave, onClose }) => {
	const [todo, setTodo] = useState(initial);
	const toggle = (kind) => setTodo(todo.includes(kind) ? todo.filter((item) => item !== kind) : [...todo, kind]);
	return (
		<div className="flex flex-wrap items-center gap-2 p-2 mt-2 rounded-lg bg-purple-50 dark:bg-gray-700">
			<span className="text-xs text-gray-600 dark:text-gray-300">Юу хийх:</span>
			{KINDS.map((kind) => (
				<button key={kind} type="button" className={kindClass(todo.includes(kind))} onClick={() => toggle(kind)}>
					{NEEDS[kind]}
				</button>
			))}
			<button className={smallButton} disabled={busy || todo.length === 0} onClick={() => onSave(todo)}>
				📌 Тушаах
			</button>
			<button className={smallButton} disabled={busy} onClick={onClose}>
				Болих
			</button>
		</div>
	);
};

const TaskRow = ({ task, plan, canAssign, busy, onVisit, onMove, onOrder, onUnorder, onRelease, onTheWay, stopDay }) => {
	const [ordering, setOrdering] = useState(false);
	const ordered = Boolean(task.plan && task.plan.ordered);
	const todo = orderOf(task);
	const locked = ordered && !canAssign;
	// Only a role with the dashboard is sent what a kiosk brings
	const perDay = typeof task.income === "number" ? task.income / (plan.rateDays || 14) : null;
	const details = [
		perDay !== null ? `≈ ${money(Math.round(perDay))}/өдөр` : null,
		task.lastVisit ? "сүүлд " + ubDayOf(task.lastVisit).slice(5) : null,
		onTheWay ? dayLabel(task.day, plan.today) : null,
	].filter(Boolean);
	return (
		<div className={"py-3 border-t border-gray-100 dark:border-gray-700" + (onTheWay ? " opacity-75" : "")}>
			<div className="flex items-start justify-between">
				<div className="min-w-0">
					<p className="font-semibold text-gray-700 truncate dark:text-gray-200">{task.name}</p>
					<p className="text-xs text-gray-500 dark:text-gray-400">{details.join(" · ")}</p>
				</div>
				<button className={primaryButton + " flex-shrink-0 ml-3"} disabled={busy} onClick={() => onVisit(task.id)}>
					Хийсэн
				</button>
			</div>
			<div>
				{task.needs.map((need) => (
					<Chip key={need.kind} className={needClass(need)}>
						{needText(need)}
					</Chip>
				))}
				{ordered && (
					<Chip className="text-purple-700 bg-purple-100 dark:bg-purple-700 dark:text-purple-100">
						📌 Тушаал{todo.length > 0 ? ": " + kindsText(todo) : ""}
						{task.plan.by ? " · " + task.plan.by : ""}
					</Chip>
				)}
				{task.plan && !ordered && (
					<Chip className="text-blue-700 bg-blue-100 dark:bg-blue-700 dark:text-blue-100">
						Төлөвлөсөн{task.plan.by ? ": " + task.plan.by : ""}
					</Chip>
				)}
			</div>
			{task.late && (
				<p className="mt-2 text-xs font-semibold text-red-600 dark:text-red-400">
					⚠ Дуусах өдөр: {dayLabel(task.runsOut, plan.today)}. Үүнээс өмнө очих хэрэгтэй.
				</p>
			)}
			<div className="flex flex-wrap items-center gap-2 mt-2">
				{onTheWay ? (
					<button className={smallButton} disabled={busy || locked} onClick={() => onMove(task, stopDay)}>
						{dayName(stopDay, plan.today)} хийх
					</button>
				) : (
					<>
						<DayPicker
							value={task.day}
							days={plan.days}
							today={plan.today}
							disabled={busy || locked}
							onChange={(day) => onMove(task, day)}
						/>
						{task.plan && !locked && (
							<button className={smallButton} disabled={busy} onClick={() => onRelease(task)}>
								↺ Автомат өдөр
							</button>
						)}
						{canAssign && !ordering && (
							<button className={smallButton} disabled={busy} onClick={() => setOrdering(true)}>
								{ordered ? "Тушаал засах" : "📌 Тушаах"}
							</button>
						)}
						{canAssign && ordered && (
							<button className={smallButton} disabled={busy} onClick={() => onUnorder(task)}>
								Тушаал цуцлах
							</button>
						)}
					</>
				)}
				{locked && <span className="text-xs text-gray-500 dark:text-gray-400">Админ тушаасан тул өөрчлөхгүй</span>}
			</div>
			{ordering && (
				<OrderPicker
					initial={ordered ? todo : [...new Set(task.needs.map((need) => need.kind))]}
					busy={busy}
					onSave={async (picked) => {
						if (await onOrder(task, picked)) setOrdering(false);
					}}
					onClose={() => setOrdering(false)}
				/>
			)}
		</div>
	);
};

const Service = ({ auth, getService, recordVisit, planKiosk, unplanKiosk }) => {
	const canAssign = canAccess(auth.user, "service.assign");
	const [plan, setPlan] = useState(null);
	const [loading, setLoading] = useState(true);
	const [chosenDay, setChosenDay] = useState(null);
	const [visiting, setVisiting] = useState(null);
	const [busy, setBusy] = useState(false);
	const [showOthers, setShowOthers] = useState(false);

	// A refresh under way while the plan changed may answer with the plan from before
	// the change, so it counts only when no change began or ended meanwhile
	const changes = useRef(0);
	const load = useCallback(
		async (quiet) => {
			const before = changes.current;
			const data = await getService(quiet);
			if (data && changes.current === before) setPlan(data);
			setLoading(false);
		},
		[getService]
	);
	useEffect(() => {
		load(false);
		const refresh = setInterval(() => load(true), REFRESH_MS);
		return () => clearInterval(refresh);
	}, [load]);

	// Every call answers with the whole plan
	const apply = async (request, message) => {
		changes.current += 1;
		setBusy(true);
		const data = await request;
		changes.current += 1;
		setBusy(false);
		if (data) {
			setPlan(data);
			if (message) toastr.success(message);
		}
		return data;
	};

	if (loading) {
		return (
			<Admin>
				<img src={spinner} className="w-16 mx-auto my-10" alt="Loading..." />
			</Admin>
		);
	}
	if (!plan) {
		return (
			<Admin>
				<h2 className="my-6 text-2xl font-semibold text-gray-700 dark:text-gray-200">Үйлчилгээ</h2>
				<p className="text-sm text-gray-600 dark:text-gray-400">Төлөвлөгөөг уншиж чадсангүй. Хуудсаа дахин ачаална уу.</p>
			</Admin>
		);
	}

	const day = plan.days.some((item) => item.day === chosenDay) ? chosenDay : plan.today;
	const current = plan.days.find((item) => item.day === day) || { day, stops: [] };
	const kioskCount = (item) => item.stops.reduce((sum, stop) => sum + stop.kiosks.length, 0);
	const tasks = plan.days.flatMap((item) => item.stops.flatMap((stop) => stop.kiosks));
	const planned = new Set(tasks.map((task) => task.id));
	// The backend sends the kiosks the most income first
	const others = plan.printers.filter((kiosk) => !planned.has(kiosk.id));
	const kioskOf = (id) => plan.printers.find((kiosk) => kiosk.id === id);
	const packing = packingOf(current.stops);

	const move = (task, toDay) => {
		const keepOrder = Boolean(task.plan && task.plan.ordered && canAssign);
		return apply(
			planKiosk(task.id, toDay, keepOrder, keepOrder ? orderOf(task) : []),
			`${task.name} → ${dayLabel(toDay, plan.today)}`
		);
	};
	const order = (task, todo) =>
		apply(
			planKiosk(task.id, task.day, true, todo),
			`${task.name} → ${dayLabel(task.day, plan.today)}: ${kindsText(todo)}, тушаалаа`
		);
	// The day stays, as a plan anyone may move; ↺ gives back the worked-out one
	const unorder = (task) =>
		apply(planKiosk(task.id, task.day, false), `${task.name}: тушаалыг цуцаллаа, өдөр нь хэвээр`);
	const release = (task) => apply(unplanKiosk(task.id), `${task.name}: өдрийг автоматаар тооцно`);
	const save = async (visit) => {
		const data = await apply(recordVisit(visit), "Хадгаллаа");
		if (data) setVisiting(null);
	};
	const rowProps = {
		plan,
		canAssign,
		busy,
		onVisit: (id) => setVisiting(id),
		onMove: move,
		onOrder: order,
		onUnorder: unorder,
		onRelease: release,
	};
	const visitingKiosk = visiting ? kioskOf(visiting) : null;
	const visitingOrder = orderOf(tasks.find((task) => task.id === visiting));

	return (
		<Admin>
			{/* min-w-0 keeps the row of day tabs from widening the page past a phone screen */}
			<div className="min-w-0">
				<h2 className="mt-6 text-2xl font-semibold text-gray-700 dark:text-gray-200">Үйлчилгээ</h2>
				<p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
					Цаас, бэх дуусахаас өмнө очих өдрүүд. Орлого ихтэй киоск эхэнд. Аль ч киоскийг хүссэн үедээ «Хийсэн» гэж бүртгэнэ.
				</p>

				<div className="sticky top-0 z-10 px-6 py-3 -mx-6 bg-gray-50 dark:bg-gray-900">
					<div className="flex gap-2 overflow-x-auto">
						{plan.days.map((item) => {
							const active = item.day === day;
							const count = kioskCount(item);
							return (
								<button
									key={item.day}
									onClick={() => setChosenDay(item.day)}
									className={
										"flex-shrink-0 px-3 py-2 text-sm font-medium leading-5 border rounded-full focus:outline-none " +
										(active
											? "text-white bg-purple-600 border-purple-600"
											: "text-gray-700 bg-white border-gray-200 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700") +
										(count === 0 && !active ? " opacity-50" : "")
									}
								>
									{dayName(item.day, plan.today)}
									<span className="ml-2 font-semibold">{count}</span>
								</button>
							);
						})}
					</div>
				</div>

				{packing.length > 0 && (
					<p className="mb-4 text-sm text-gray-700 dark:text-gray-300">
						<span className="font-semibold">Авч явах:</span> {packing.join(" · ")}
					</p>
				)}

				{current.stops.length === 0 ? (
					<p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
						{dayLabel(day, plan.today)}: очих киоск алга 🎉
					</p>
				) : (
					current.stops.map((stop, index) => (
						<section key={stop.location} className="p-4 mb-4 bg-white rounded-lg shadow-xs dark:bg-gray-800">
							<p className="mb-1 font-semibold text-gray-700 dark:text-gray-200">
								<span className="inline-flex items-center justify-center w-6 h-6 mr-2 text-xs text-white bg-purple-600 rounded-full">
									{index + 1}
								</span>
								{stop.location}
								{stop.ordered && <span className="ml-2 text-xs text-purple-600 dark:text-purple-300">📌 тушаал</span>}
							</p>
							{stop.kiosks.map((task) => (
								<TaskRow key={task.id} task={task} {...rowProps} />
							))}
							{stop.onTheWay.length > 0 && (
								<>
									<p className="mt-3 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
										Замдаа хийж болох
									</p>
									{stop.onTheWay.map((task) => (
										<TaskRow key={task.id} task={task} {...rowProps} onTheWay stopDay={day} />
									))}
								</>
							)}
						</section>
					))
				)}

				<section className="mb-8">
					<button
						className="text-sm font-semibold text-purple-600 dark:text-purple-300 focus:outline-none"
						onClick={() => setShowOthers(!showOthers)}
					>
						{showOthers ? "▾" : "▸"} Бусад киоск ({others.length})
					</button>
					{showOthers && (
						<div className="p-4 mt-3 bg-white rounded-lg shadow-xs dark:bg-gray-800">
							<p className="text-xs text-gray-500 dark:text-gray-400">
								Удахгүй дуусахгүй, эсвэл сүүлийн {plan.rateDays} хоногт хэвлээгүй киоскууд. Санаачлагаараа хийсэн бол «Хийсэн»,
								урьдчилж очих бол өдрөө сонгоно.
							</p>
							{others.map((kiosk) => (
								<div key={kiosk.id} className="flex items-center justify-between py-3 border-t border-gray-100 dark:border-gray-700">
									<div className="min-w-0">
										<p className="font-semibold text-gray-700 truncate dark:text-gray-200">{kiosk.name}</p>
										<p className="text-xs text-gray-500 dark:text-gray-400">
											{kiosk.location} · A4 {kiosk.paper.sheets}
											{kiosk.black ? ` · хар ${kiosk.black.level}/${kiosk.black.marks}` : ""}
											{kiosk.color ? ` · өнгө ${kiosk.color.level}/${kiosk.color.marks}` : ""}
											{kiosk.idle ? ` · ${plan.rateDays} хоног хэвлээгүй` : ""}
										</p>
									</div>
									<div className="flex items-center flex-shrink-0 gap-2 ml-3">
										<DayPicker
											value=""
											days={plan.days}
											today={plan.today}
											disabled={busy}
											onChange={(toDay) => move({ id: kiosk.id, name: kiosk.name, plan: null }, toDay)}
										/>
										<button className={smallButton} disabled={busy} onClick={() => setVisiting(kiosk.id)}>
											Хийсэн
										</button>
									</div>
								</div>
							))}
						</div>
					)}
				</section>

				{visitingKiosk && (
					<VisitForm
						kiosk={visitingKiosk}
						marks={plan.marks}
						order={visitingOrder.length > 0 ? kindsText(visitingOrder) : ""}
						busy={busy}
						onSave={save}
						onClose={() => setVisiting(null)}
					/>
				)}
			</div>
		</Admin>
	);
};

Service.propTypes = {
	auth: PropTypes.object.isRequired,
	getService: PropTypes.func.isRequired,
	recordVisit: PropTypes.func.isRequired,
	planKiosk: PropTypes.func.isRequired,
	unplanKiosk: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
	auth: state.auth,
});

export default connect(mapStateToProps, { getService, recordVisit, planKiosk, unplanKiosk })(Service);
